import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface WorkshopMasterDataQuery {
  clientId?: string;
  dealerCode?: string;
  workshopType?: string[];
  year?: string;
  month?: string[];
  cbfName?: string[];
  fuzzySearch?: string;
  selectedClientId?: string;
  pageNum?: number;
  pageSize?: number;
}

export interface WorkshopMasterDataStatsVO {
  clientStatus: string;
  statusCount: number;
  totalClientCount: number;
}

export interface WorkshopRemainPointsGrowthVO {
  currentWeekClientCount: number;
  growthRate: number;
}

export interface WorkshopTargetVO {
  workshopType: string;
  totalWorkshop: number;
  totalMtdTurnover: number;
  gtvPerClientType: number;
  targetAchWorkshop: number;
}

export interface WorkshopAmountSumVO {
  clientId: string;
  clientName: string;
  amountSummary: string;
}

export interface PerformanceDistributionVO {
  activityRate: number;
  complianceRate: number;
  workshopAmountSumVOS: WorkshopAmountSumVO[];
}

export interface WorkshopMasterDataVO {
  clientId: string;
  dealerCode: string;
  snapYear: string;
  workshopType: string;
  clientName: string;
  marketName: string;
  detailAddress: string;
  clientStatus: string;
  lastRedeemTime: string;
  remainPoints: number;
  clientTypeExtra: string;
  gtvPerClientType: string;
  cbfName: string;
  fromApp: string;
  mtdTurnover: string;
  mtdQty: string;
}

export interface WorkshopSelloutCbfSummaryVO {
  clientId: string;
  clientName: string;
  cbfName: string;
  cbfCNName: string;
  totalMtdTurnover: number;
  totalMtdQty: number;
  yom: number | null;
  yoy: number | null;
}

export interface WorkshopFilterVO {
  yearList: string[];
  cbfNames: string[];
  workshopTypes: string[];
}

export interface PageResult<T> {
  records: T[];
  total: number;
  size: number;
  current: number;
  pages: number;
}

export interface WorkshopVO {
  workshopMasterDataStatsVO: WorkshopMasterDataStatsVO[];
  growthVO: WorkshopRemainPointsGrowthVO;
  workshopTargetVO: WorkshopTargetVO[];
  salesStoreTargetVO: WorkshopTargetVO[];
  performanceDistributionVO: PerformanceDistributionVO;
  workshopMasterDataVO: PageResult<WorkshopMasterDataVO>;
  workshopSelloutCbfSummaryVO: WorkshopSelloutCbfSummaryVO[];
}

export interface WorkshopTargetGroupVO {
  workshopTargetVO: WorkshopTargetVO[];
  salesStoreTargetVO: WorkshopTargetVO[];
}

@Injectable({
  providedIn: 'root'
})
export class WorkshopService {
  private readonly prefix = '/eshop_backend/workshop';

  constructor(private http: HttpClient) {}

  /**
   * @deprecated 该接口因一次性组装数据过多导致响应过慢，请改用拆分后的独立接口：
   * getStatusStats / getGrowth / getTargetGroup / getPerformanceDistribution / getMasterDataPage
   */
  getMasterData(query: WorkshopMasterDataQuery): Observable<WorkshopVO> {
    return this.http.post<any>(this.prefix + '/masterdata', query).pipe(
      map(res => res.rtnData)
    );
  }

  getStatusStats(query: WorkshopMasterDataQuery): Observable<WorkshopMasterDataStatsVO[]> {
    return this.http.post<any>(this.prefix + '/status-stats', query).pipe(
      map(res => res.rtnData || [])
    );
  }

  getGrowth(query: WorkshopMasterDataQuery): Observable<WorkshopRemainPointsGrowthVO> {
    return this.http.post<any>(this.prefix + '/growth', query).pipe(
      map(res => res.rtnData)
    );
  }

  getTargetGroup(query: WorkshopMasterDataQuery): Observable<WorkshopTargetGroupVO> {
    return this.http.post<any>(this.prefix + '/target', query).pipe(
      map(res => res.rtnData || { workshopTargetVO: [], salesStoreTargetVO: [] })
    );
  }

  getPerformanceDistribution(query: WorkshopMasterDataQuery): Observable<PerformanceDistributionVO> {
    return this.http.post<any>(this.prefix + '/performance-distribution', query).pipe(
      map(res => res.rtnData)
    );
  }

  getMasterDataPage(query: WorkshopMasterDataQuery): Observable<PageResult<WorkshopMasterDataVO>> {
    return this.http.post<any>(this.prefix + '/master-data-page', query).pipe(
      map(res => res.rtnData)
    );
  }

  exportMasterData(query: WorkshopMasterDataQuery): Observable<HttpResponse<Blob>> {
    return this.http.post(this.prefix + '/master-data-export', query, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  getCbfSummary(query: WorkshopMasterDataQuery): Observable<PageResult<WorkshopSelloutCbfSummaryVO>> {
    return this.http.post<any>(this.prefix + '/cbf-summary', query).pipe(
      map(res => res.rtnData || { records: [], total: 0, size: query.pageSize ?? 10, current: query.pageNum ?? 1, pages: 0 })
    );
  }

  getFilterOptions(): Observable<WorkshopFilterVO> {
    return this.http.get<any>(this.prefix + '/filter-options').pipe(
      map(res => res.rtnData || { yearList: [], cbfNames: [], workshopTypes: [] })
    );
  }
}
